/**
 * Serviço de Endpoints do Sistema MEDICARE
 * Gerencia todas as operações relacionadas a endpoints
 */

import { ApiService, buildQueryString } from './api';
import {
  ApiResponse,
  PaginatedResponse,
} from '../types';

// Interface para endpoint
export interface Endpoint {
  id: string;
  nome: string;
  url: string;
  metodo: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  descricao?: string;
  ativo: boolean;
  requer_autenticacao: boolean;
  tipo_auth: string;
  usuario?: string;
  senha?: string;
  data_criacao: string;
  data_atualizacao: string;
}

// Interface para criar endpoint
export interface CriarEndpointData {
  nome: string;
  url: string;
  metodo: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  descricao?: string;
  requer_autenticacao?: boolean;
  tipo_auth?: string;
  usuario?: string;
  senha?: string;
}

// Interface para atualizar endpoint
export interface AtualizarEndpointData {
  nome?: string;
  url?: string;
  metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  descricao?: string;
  ativo?: boolean;
  requer_autenticacao?: boolean;
  tipo_auth?: string;
  usuario?: string;
  senha?: string;
}

// Interface para filtros
export interface FiltroEndpoints {
  nome?: string;
  busca?: string;
  metodo?: string;
  ativo?: boolean;
  requer_autenticacao?: boolean;
}

export class EndpointsService {
  private static baseUrl = '/admin/endpoints';
  private static consultasUrl = '/consultas/endpoints';

  /**
   * Listar endpoints com paginação e filtros
   * Para médicos: usa rota de consultas (apenas endpoints ativos)
   * Para admins: usa rota administrativa (todos os endpoints)
   */
  static async listar(
    page: number = 1,
    limit: number = 20,
    filtros?: FiltroEndpoints
  ): Promise<{ data: Endpoint[]; total: number; totalPages: number }> {
    const params = {
      page: page.toString(),
      limit: limit.toString(),
      ...filtros,
    };

    const queryString = buildQueryString(params);
    
    // Tentar primeiro a rota de consultas (para médicos)
    try {
      const response = await ApiService.get(
        `${this.consultasUrl}${queryString ? '?' + queryString : ''}`
      );
      
      if (response.success && response.data) {
        return {
          data: response.data.dados,
          total: response.data.paginacao?.total_registros || 0,
          totalPages: response.data.paginacao?.total_paginas || 1
        };
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error: any) {
      // Se falhar (403), tentar a rota administrativa (para admins)
      if (error.response?.status === 403 || !error.response) {
        const response = await ApiService.get(
          `${this.baseUrl}${queryString ? '?' + queryString : ''}`
        );
        
        if (response.success && response.data) {
          return {
            data: response.data.dados,
            total: response.data.paginacao?.total_registros || 0,
            totalPages: response.data.paginacao?.total_paginas || 1
          };
        } else {
          throw new Error('Resposta inválida do servidor');
        }
      }
      throw error;
    }
  }

  /**
   * Buscar endpoint por ID
   */
  static async buscarPorId(id: string): Promise<Endpoint> {
    const response = await ApiService.get(
      `${this.baseUrl}/${id}`
    );
    return response.data.dados;
  }

  /**
   * Criar novo endpoint
   */
  static async criar(dados: CriarEndpointData): Promise<Endpoint> {
    const response = await ApiService.post(
      this.baseUrl,
      dados
    );
    return response.data.dados;
  }

  /**
   * Atualizar endpoint
   */
  static async atualizar(
    id: string,
    dados: AtualizarEndpointData
  ): Promise<Endpoint> {
    const response = await ApiService.put(
      `${this.baseUrl}/${id}`,
      dados
    );
    return response.data.dados;
  }

  /**
   * Deletar endpoint
   */
  static async deletar(id: string): Promise<void> {
    await ApiService.delete(
      `${this.baseUrl}/${id}`
    );
  }

  /**
   * Ativar/Desativar endpoint
   */
  static async alterarStatus(
    id: string,
    ativo: boolean
  ): Promise<Endpoint> {
    return this.atualizar(id, { ativo });
  }

  /**
   * Testar conectividade do endpoint
   */
  static async testarConectividade(id: string): Promise<any> {
    const response = await ApiService.post(
      `${this.baseUrl}/${id}/testar`
    );
    return response.data.dados;
  }
}

export default EndpointsService;